/** Birth registration application at a mission abroad (দূতাবাস ফরম-১, বিধি ৬) — data helpers. */

import { numberToBengaliWords } from '../../../../../utils/currencyWords';
import {
    displayField,
    formatDateField,
    parseDateField,
    toBengaliDigits,
} from '../voter-migration-form/voterMigrationFormEngine';

export { displayField, formatDateField, parseDateField, toBengaliDigits };

/** The form only asks for the two the table prints. */
export const GENDER_KEYS = ['female', 'male'];

/** ৫'s relation tick boxes, then the free-text "অন্যান্য". */
export const RELATION_KEYS = ['father', 'mother', 'other'];

/** Most applicants at a mission are Bangladeshi parents, so it starts filled. */
export const DEFAULT_NATIONALITY = 'বাংলাদেশী';

const BN_MONTHS = [
    'জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন',
    'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর',
];

export const INITIAL_EMBASSY_BIRTH_FORM = {
    // ১ — নিবন্ধনাধীন ব্যক্তি
    registrantName: '',
    birthDate: '',
    gender: '',
    birthPlace: '',
    passportNo: '',
    // ২ — পিতা ও মাতা
    fatherName: '',
    fatherNationality: DEFAULT_NATIONALITY,
    fatherPassport: '',
    motherName: '',
    motherNationality: DEFAULT_NATIONALITY,
    motherPassport: '',
    // ৩-৪ — ঠিকানা
    permanentAddress: '',
    presentAddress: '',
    // ৫ — প্রত্যয়ন
    declarantName: '',
    declarantRelation: '',
    declarantRelationOther: '',
    applicationDate: '',
};

/** Blank on a correct form: passports are "যদি থাকে", and "অন্যান্য" only for other relations. */
const OPTIONAL_KEYS = new Set([
    'passportNo',
    'fatherPassport',
    'motherPassport',
    'declarantRelationOther',
]);

const FORM_SNAPSHOT_KEY = 'embassy-birth-form-last-snapshot';

/** @returns {typeof INITIAL_EMBASSY_BIRTH_FORM | null} */
export function getEmbassyBirthFormSnapshot() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        return { ...INITIAL_EMBASSY_BIRTH_FORM, ...JSON.parse(raw) };
    } catch {
        return null;
    }
}

export function saveEmbassyBirthFormSnapshot(form) {
    if (typeof window === 'undefined' || !form) return;
    try {
        const payload = {};
        for (const key of Object.keys(INITIAL_EMBASSY_BIRTH_FORM)) {
            payload[key] = displayField(form[key]);
        }
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

/**
 * Every blank still empty, named the way the form asks for it — what the
 * download confirmation lists back. "অন্যান্য" is only missing when it was the
 * relation picked.
 *
 * @param {typeof INITIAL_EMBASSY_BIRTH_FORM} form
 * @param {(key: string, opts?: object) => string} t
 */
export function getEmptyEmbassyBirthFields(form, t) {
    const empty = Object.keys(INITIAL_EMBASSY_BIRTH_FORM)
        .filter((key) => !OPTIONAL_KEYS.has(key) && !displayField(form?.[key]));
    if (form?.declarantRelation === 'other' && !displayField(form?.declarantRelationOther)) {
        empty.push('declarantRelationOther');
    }
    return empty.map((key) => t(`embassyBirthForm.fields.${key}`));
}

export function isEmbassyBirthFormComplete(form, t) {
    return getEmptyEmbassyBirthFields(form, t).length === 0;
}

/**
 * A date split into the form's দিন / মাস / বৎসর cells, in Bengali digits, and
 * — for the birth date's "কথায়" row — in words.
 * @param {Date | null} date
 */
export function splitDate(date) {
    if (!date) return { day: '', month: '', year: '', dayWords: '', monthWords: '', yearWords: '' };
    const pad = (n) => String(n).padStart(2, '0');
    return {
        day: toBengaliDigits(pad(date.getDate())),
        month: toBengaliDigits(pad(date.getMonth() + 1)),
        year: toBengaliDigits(date.getFullYear()),
        dayWords: numberToBengaliWords(date.getDate()),
        monthWords: BN_MONTHS[date.getMonth()],
        yearWords: numberToBengaliWords(date.getFullYear()),
    };
}

/** Everything the preview prints, already trimmed, plus the split-out dates. */
export function buildEmbassyBirthForm(form) {
    const out = {};
    for (const key of Object.keys(INITIAL_EMBASSY_BIRTH_FORM)) {
        out[key] = displayField(form?.[key]);
    }

    const birth = splitDate(parseDateField(out.birthDate));
    out.birthDay = birth.day;
    out.birthMonth = birth.month;
    out.birthYear = birth.year;
    out.birthDayWords = birth.dayWords;
    out.birthMonthWords = birth.monthWords;
    out.birthYearWords = birth.yearWords;

    const applied = splitDate(parseDateField(out.applicationDate));
    out.applicationDay = applied.day;
    out.applicationMonth = applied.month;
    out.applicationYear = applied.year;

    // "অন্যান্য" is written only when neither printed box applies.
    if (out.declarantRelation !== 'other') out.declarantRelationOther = '';
    return out;
}
